import React, { useState, useEffect, useCallback } from 'react';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../App';
import useBank from './useBank';
import { useFireAuth } from '../FireAuthContext';
import { FiPlay } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { FaHammer } from 'react-icons/fa';
import './Inventory.css';
import ScratchCard5 from './ScratchCard5';
import ScratchCoin from './ScratchCoin';
import ScratchCardGold from './ScratchCardGold';
import Modal from 'react-modal';

const scratchCardItems = [
  { name: 'Scratchcard', component: ScratchCard5 },
  { name: 'Scratchcoin', component: ScratchCoin },
  { name: 'Golden Scratchcard', component: ScratchCardGold },
];

Modal.setAppElement('#root');

const Inventory = () => {
  const [inventoryItems, setInventoryItems] = useState([]);
  const { removeItem, refreshUser } = useBank();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [GameComponent, setGameComponent] = useState(null);
  const { user } = useFireAuth();
  const [expandedStack, setExpandedStack] = useState(null);

  const fetchInventoryItems = useCallback(async () => {
    if (!user || !user.inventory || user.inventory.length === 0) {
      setInventoryItems([]);
      return;
    }

    try {
      const itemsData = await Promise.all(
        user.inventory.map(async (itemId) => {
          try {
            const originalItemId = itemId.split('_').slice(-1)[0];
            const itemDocRef = doc(db, 'items', originalItemId);
            const itemDocSnap = await getDoc(itemDocRef);

            if (itemDocSnap.exists()) {
              return {
                id: itemId,
                ...itemDocSnap.data()
              };
            } else {
              console.error(`Item with id ${originalItemId} does not exist`);
              return null;
            }
          } catch (error) {
            console.error(`Error fetching item ${itemId}:`, error);
            return null;
          }
        })
      );

      const validItemsData = itemsData.filter(item => item !== null);
      setInventoryItems(validItemsData);
    } catch (error) {
      console.error('Error fetching inventory items:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchInventoryItems();
  }, [fetchInventoryItems]);

  const updateInventory = useCallback(async () => {
    await refreshUser();
    await fetchInventoryItems();
  }, [refreshUser, fetchInventoryItems]);

  const scratchCard = (cardType) => {
    console.log(`Scratching ${cardType} card`);
    const selectedCard = scratchCardItems.find(card => card.name === cardType);
    if (selectedCard) {
      setGameComponent(() => selectedCard.component);
      setIsModalOpen(true);
    }
  };

  const handleUseItem = async (itemId) => {
    try {
      const updatedUser = await removeItem(itemId);
      if (updatedUser) {
        setInventoryItems(prevItems => prevItems.filter(item => item.id !== itemId));
        const selectedInventoryItem = inventoryItems.find(item => item.id === itemId);
        if (selectedInventoryItem) {
          scratchCard(selectedInventoryItem.name);
        }
      } else {
        alert('This item is no longer in your inventory.');
        window.location.reload();
      }
    } catch (error) {
      console.error('Error using item:', error);
      alert('An error occurred while using the item. Please try again.');
    }
  };

  const closeModal = async () => {
    setIsModalOpen(false);
    setGameComponent(null);
    await updateInventory();
  };

  const renderInventoryItem = (item, index) => (
    <div key={item.id} className="inventory-item">
      <div className="item-image-container">
        <img src={item.imageUrl} alt={item.name} className="item-image" />
      </div>
      <div className="item-details">
        <div>
          <p className="item-name">{item.name}</p>
          <p className="item-value">Value: {item.currency === 'money' ? `💴 ${item.price}` : `🪙 ${item.price}`}</p>
        </div>
        {scratchCardItems.some(card => card.name === item.name) && (
          <div className="scratch-card-button">
            <button onClick={() => handleUseItem(item.id)}>
              <FiPlay /> Scratch
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const handleToggleStack = (name) => {
    setExpandedStack(prevState => prevState === name ? null : name);
  };

  const renderStackedItems = (item) => {
    const itemsOfType = inventoryItems.filter(i => i.name === item.name);
    const itemCount = itemsOfType.length;
    const totalValue = itemsOfType.reduce((acc, i) => acc + i.price, 0);

    if (itemCount < 3) {
      return itemsOfType.map(renderInventoryItem);
    }

    return (
      <React.Fragment key={item.name}>
        <div className={`inventory-item ${expandedStack === item.name ? 'expanded' : ''}`} onClick={() => handleToggleStack(item.name)}>
          <div className="item-image-container">
            <img src={item.imageUrl} alt={item.name} className="item-image" />
          </div>
          <div className="item-details">
            <div>
              <p className="item-name">{item.name} <span className="item-count">(x{itemCount})</span></p>
              <p className="item-value">Total Value: {item.currency === 'money' ? `💴 ${totalValue}` : `🪙 ${totalValue}`}</p>
            </div>
          </div>
        </div>
        {expandedStack === item.name && itemsOfType.map(renderInventoryItem)}
      </React.Fragment>
    );
  };

  return (
    <div className="inventory-section">
      <h3>Inventory</h3>
      {inventoryItems.length > 0 ? (
        <div className="inventory-grid">
          {[...new Set(inventoryItems.map(item => item.name))].map(itemName => {
            const item = inventoryItems.find(i => i.name === itemName);
            return renderStackedItems(item);
          })}
        </div>
      ) : (
        <p>Your inventory is empty.</p>
      )}
      <Link to="/portal/craft" className="crafting-link">
        <FaHammer /> Craft Items
      </Link>
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel="Scratch Card Game"
        className="scratch-card-modal"
        overlayClassName="scratch-card-overlay"
        shouldCloseOnOverlayClick={false}
      >
        {GameComponent && <GameComponent refreshUser={updateInventory} />}
        <button onClick={closeModal} className="close-modal-button">Close</button>
      </Modal>
    </div>
  );
};

export default Inventory;
